/**
 * SalesOverview — net sales per day.
 *
 * A single series over time, so: area, one hue, no legend (the title names it).
 * Hand-rolled SVG rather than a charting library — this app already draws its
 * sparkline this way, it ships nothing extra to an offline-first bundle, and
 * the whole mark is forty lines.
 *
 * Carries a crosshair and tooltip because an SVG chart on a screen IS
 * interactive and a reader will ask "which day was that?". The value is also
 * available as a table for anyone who cannot use hover.
 */
import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Money } from '@/components/ui/numeric';
import { SEQ_HUE } from './chartTokens';

export interface DayPoint {
  /** Local midnight, epoch ms. */
  day: number;
  total: number;
}

const W = 720;
const H = 180;
const PAD_L = 8;
const PAD_R = 8;
const PAD_T = 12;
const PAD_B = 26;

export function SalesOverview({ data }: { data: DayPoint[] }) {
  const [hover, setHover] = useState<number | null>(null);

  const { line, area, xs, ys, max } = useMemo(() => {
    const max = Math.max(...data.map((d) => d.total), 1);
    const innerW = W - PAD_L - PAD_R;
    const innerH = H - PAD_T - PAD_B;
    const step = data.length > 1 ? innerW / (data.length - 1) : innerW;
    const xs = data.map((_, i) => PAD_L + i * step);
    const ys = data.map((d) => PAD_T + innerH - (d.total / max) * innerH);
    const line = xs.map((x, i) => `${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
    const area = `${PAD_L},${PAD_T + innerH} ${line} ${(PAD_L + (data.length - 1) * step).toFixed(1)},${PAD_T + innerH}`;
    return { line, area, xs, ys, max };
  }, [data]);

  const hasSales = data.some((d) => d.total > 0);
  const active = hover != null ? data[hover] : null;

  return (
    <Card className="p-5">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h2 className="text-heading font-medium text-fg-primary">Sales overview</h2>
        <span className="text-label text-fg-tertiary">Last {data.length} days</span>
      </div>
      <p className="mb-4 text-label text-fg-tertiary">
        {hasSales ? 'Net sales per day' : 'No sales in this period yet'}
      </p>

      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          role="img"
          aria-label={`Net sales per day for the last ${data.length} days`}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={SEQ_HUE} stopOpacity="0.20" />
              <stop offset="100%" stopColor={SEQ_HUE} stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Baseline only. A full grid would out-weigh a single thin series. */}
          <line
            x1={PAD_L} y1={H - PAD_B} x2={W - PAD_R} y2={H - PAD_B}
            stroke="currentColor" strokeWidth={1} className="text-stroke-hairline/20"
          />

          <polygon points={area} fill="url(#salesFill)" />
          <polyline
            points={line}
            fill="none"
            stroke={SEQ_HUE}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />

          {active && hover != null && (
            <g>
              <line
                x1={xs[hover]} y1={PAD_T} x2={xs[hover]} y2={H - PAD_B}
                stroke={SEQ_HUE} strokeWidth={1} strokeDasharray="3 3" opacity={0.5}
              />
              {/* Ringed in the surface colour so the marker reads on the fill. */}
              <circle cx={xs[hover]} cy={ys[hover]} r={5} fill={SEQ_HUE} stroke="#fff" strokeWidth={2} />
            </g>
          )}

          {/* Hit targets are full-height columns, far bigger than the marks. */}
          {xs.map((x, i) => (
            <rect
              key={i}
              x={x - (W / Math.max(data.length, 1)) / 2}
              y={0}
              width={W / Math.max(data.length, 1)}
              height={H - PAD_B}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          ))}

          {/* Ends only — a label per point is noise. */}
          {data.length > 1 && (
            <>
              <text x={PAD_L} y={H - 8} className="fill-fg-tertiary text-[11px]">
                {fmtDay(data[0].day)}
              </text>
              <text x={W - PAD_R} y={H - 8} textAnchor="end" className="fill-fg-tertiary text-[11px]">
                {fmtDay(data[data.length - 1].day)}
              </text>
            </>
          )}
        </svg>

        {active && (
          <div className="pointer-events-none absolute left-0 top-0 rounded-md border border-stroke-hairline/15 bg-bg-surface px-2.5 py-1.5 shadow-e2">
            <div className="text-label text-fg-tertiary">{fmtDay(active.day, true)}</div>
            <div className="text-body font-semibold text-fg-primary">
              <Money value={active.total} />
            </div>
          </div>
        )}
      </div>

      <p className="mt-2 text-label text-fg-tertiary">
        Peak day <Money value={max} className="text-fg-secondary" />
      </p>
    </Card>
  );
}

function fmtDay(ms: number, long = false): string {
  return new Date(ms).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    ...(long ? { weekday: 'short' } : {}),
  });
}
